import React, { useEffect, useMemo, useState, useCallback } from 'react'
import { I18n } from 'aws-amplify'
import { putSuccessUserDetail as ToastPutSuccess, putErrorUserDetail as ToastPutError} from './../../shared/utils/toast';

import {Button, Spinner, Modal, Form, Alert} from 'react-bootstrap'

import { ConstantEquals, USER } from './../../shared/constants/index'
import { ValidMessage, ValidActive } from './Validate'
import { putUserActive } from './api/User'

export const Active = (props) => {
  const { 
    userId, 
    active: 
    propActive, 
    doneUpdatedAt, 
    doneUpdateActive 
  } = props

  // 表示状態（サーバー確定値）
  const [active, setActive] = useState(!!propActive)

  // モーダル＆通信状態
  const [show, setShow] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  // 確認入力
  const [confirmText, setConfirmText] = useState('')

  // props変化に追随
  useEffect(() => {
    setActive(!!propActive)
  }, [propActive])

  // 現在の状態に応じた確認ワード
  const confirmWord = useMemo(() => (active ? 'inactive' : 'active'), [active])

  // 実行可否
  const canSubmit = useMemo(() => {
    if (isLoading) return false
    return confirmText.trim().toLowerCase() === confirmWord
  }, [confirmText, confirmWord, isLoading])

  
  // モーダル開閉
  const openModal = () => {
    if (isLoading) return
    setConfirmText('')
    setShow(true)
  }
  const closeModal = () => {
    if (isLoading) return
    setShow(false)
    setConfirmText('')
  }

  // レスポンスの軽量パース（null/型安全）
  const parseUpdateUser = useCallback((updateUser) => {
    const res = { done: false, active: active, updatedAt: '' }
    if (!updateUser || typeof updateUser !== 'object') {
      res.done = true
      return res
    }

    // active
    const nextActive = updateUser?.active
    const validCode = ValidActive(nextActive)
    if (ConstantEquals(validCode, USER.VALIDATE.CLEAR)) {
      res.active = !!nextActive
    } else {
      // サーバーが不正値を返した場合の保険
      res.active = active
    }

    // updatedAt
    res.updatedAt = updateUser?.updatedAt ?? ''

    res.done = true
    return res
  }, [active])

    // 送信
  const submitChange = async () => {
    if (!canSubmit) return
    setIsLoading(true)

    const nextActive = !active
    const input = { id: userId, active: nextActive }

    try {
      const resp = await putUserActive(input)
      const parsed = parseUpdateUser(resp?.data?.updateUser)

      if (!parsed.done) {
        ToastPutError()
        return
      }

      setActive(parsed.active)
      ToastPutSuccess()

      // 親へ通知
      if (typeof doneUpdatedAt === 'function') doneUpdatedAt(parsed.updatedAt)
      if (typeof doneUpdateActive === 'function') doneUpdateActive(parsed.active)

      closeModal()
    } catch (e) {
      ToastPutError()
    } finally {
      setIsLoading(false)
    }
  }

  // キー操作（Enter=実行 / Esc=閉）
  const onKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      if (canSubmit) submitChange()
    } else if (e.key === 'Escape') {
      e.preventDefault()
      closeModal()
    }
  }

  return (
    <>
      <Button
        variant={active ? 'danger' : 'success'}
        onClick={openModal}
        disabled={isLoading}
        aria-label={active ? I18n.get('sub_title_user_active_disabled') : I18n.get('sub_title_user_active_enabled')}
      >
        {active ? I18n.get('sub_title_user_active_disabled') : I18n.get('sub_title_user_active_enabled')}
      </Button>

      <ConfirmActiveModal
        show={show}
        isLoading={isLoading}
        active={active}
        confirmWord={confirmWord}
        confirmText={confirmText}
        onChangeConfirm={(v) => setConfirmText(v)}
        onSubmit={submitChange}
        onClose={closeModal}
        onKeyDown={onKeyDown}
      />
    </>
  )
}

/** モーダル：アクティブ切替 */
const ConfirmActiveModal = ({
  show,
  isLoading,
  active,
  confirmWord,
  confirmText,
  onChangeConfirm,
  onSubmit,
  onClose,
  onKeyDown
}) => {
  return (
    <Modal
      show={show}
      onHide={onClose}
      size="lg"
      aria-labelledby="active-toggle-title"
      centered
      backdrop="static"
      keyboard={false}
    >
      <Modal.Header closeButton>
        <Modal.Title id="active-toggle-title">
          {active ? I18n.get('sub_title_user_active_disabled') : I18n.get('sub_title_user_active_enabled')}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {active && (
          <Alert variant="warning" className="mb-3">
            {I18n.get('alert_user_active_note')}
          </Alert>
        )}
        <p className="mb-2">
          {`${I18n.get('general_confirm_prefix')} ${confirmWord} ${I18n.get('general_confirm_suffix')}`}
        </p>
        <Form.Group controlId="formConfirm">
          <Form.Control
            type="text"
            placeholder={active ? 'Enter inactive' : 'Enter active'}
            value={confirmText}
            onChange={(e) => onChangeConfirm(e.target.value)}
            onKeyDown={onKeyDown}
            disabled={isLoading}
            aria-label="確認入力"
            autoFocus
          />
          {/* ルール明示 */}
          <div className="text-muted small mt-1">
            {active
              ? I18n.get('general_type_inactive_to_confirm') || '“inactive” と入力すると無効化できます'
              : I18n.get('general_type_active_to_confirm') || '“active” と入力すると有効化できます'}
          </div>
        </Form.Group>
      </Modal.Body>

      <Modal.Footer>
        {isLoading ? (
          <>
            <Button variant={active ? 'danger' : 'success'} disabled>
              <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />{' '}
              {active ? I18n.get('form_user_active_disabled') : I18n.get('form_user_active_enabled')}
            </Button>
            {' '}
            <Button variant="secondary" disabled>
              {I18n.get('general_cancel')}
            </Button>
          </>
        ) : (
          <>
            <Button
              variant={active ? 'danger' : 'success'}
              onClick={onSubmit}
              disabled={!confirmText || confirmText.trim().toLowerCase() !== confirmWord}
            >
              {active ? I18n.get('form_user_active_disabled') : I18n.get('form_user_active_enabled')}
            </Button>
            {' '}
            <Button variant="secondary" onClick={onClose}>
              {I18n.get('general_cancel')}
            </Button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  )
}